<template>
  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200 font-sans"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <div class="flex justify-between items-center border-b pb-5 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-0.5">
        <h1 class="text-xl font-semibold tracking-tight transition-colors duration-200" :class="isDark ? 'text-white' : 'text-zinc-900'">
          {{ isEditMode ? 'Modifier la catégorie' : 'Nouvelle catégorie' }}
        </h1>
        <p class="text-xs font-medium transition-colors duration-200" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          {{ isEditMode ? 'Mise à jour des informations de cette section.' : 'Organisez votre catalogue en créant une nouvelle famille de services.' }}
        </p>
      </div>
      <button @click="router.push('/services')" 
        class="px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer border shadow-sm flex items-center gap-2"
        :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-300' : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-600'">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/></svg>
        Retour
      </button>
    </div>

    <div class="max-w-2xl space-y-6">
      <div class="border rounded-2xl p-6 space-y-4 transition-colors"
        :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200 shadow-sm'">
        
        <div class="grid gap-4">
          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Nom de la catégorie *</label>
            <input v-model="form.nom" type="text" placeholder="Ex: Soins Visage" 
              class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all"
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 focus:border-zinc-300 shadow-sm'" />
          </div>

          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Description</label>
            <textarea v-model="form.description" rows="2" placeholder="Brève description de cette famille de prestations..." 
              class="w-full p-4 rounded-xl border text-xs font-medium focus:outline-none resize-none transition-all"
              :class="isDark ? 'border-zinc-800 bg-zinc-950 text-white placeholder-zinc-700' : 'border-zinc-200 bg-white text-zinc-800 placeholder-zinc-400 shadow-sm'"></textarea>
          </div>
        </div>
      </div>

      <div class="flex gap-3">
        <button @click="router.push('/services')" 
          class="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer border shadow-sm"
          :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-400' : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-600'">
          Annuler
        </button>
        <button @click="handleSubmit" :disabled="isSubmitting" 
          class="flex-1 px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 disabled:opacity-50 cursor-pointer shadow-sm"
          :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
          {{ isSubmitting ? (isEditMode ? 'Mise à jour...' : 'Création...') : (isEditMode ? 'Modifier la catégorie' : 'Créer la catégorie') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/api'
import { useTheme } from '@/composables/useTheme';

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();
const router = useRouter()
const route = useRoute()
const isSubmitting = ref(false)
const isEditMode = ref(false)
const editId = ref(null)

const form = reactive({
  nom: '',
  description: ''
})

onMounted(async () => {
  // Check if we're in edit mode
  if (route.query.edit) {
    isEditMode.value = true
    editId.value = route.query.edit
    await loadCategoryData(editId.value)
  }
})

const loadCategoryData = async (id) => {
  try {
    const response = await api.services.getCategoryById(id)
    const category = response.data

    Object.assign(form, {
      nom: category.nom || '',
      description: category.description || ''
    })
  } catch (error) {
    console.error('Erreur lors du chargement de la catégorie:', error)
    toast.error('Erreur lors du chargement de la catégorie')
  }
}

const handleSubmit = async () => {
  if (!form.nom) {
    toast.error('Le nom est obligatoire', { position: 'top-right' })
    return
  }

  try {
    isSubmitting.value = true

    const categoryData = {
      nom: form.nom,
      description: form.description,
      statut: 'actif'
    }

    if (isEditMode.value) {
      await api.services.updateCategory(editId.value, categoryData)
      toast.success('Catégorie modifiée avec succès !', { position: 'top-right' })
    } else {
      await api.services.createCategory(categoryData)
      toast.success('Catégorie créée avec succès !', { position: 'top-right' })
    }

    setTimeout(() => router.push('/services'), 500)
  } catch (error) {
    console.error(error)
    toast.error(`Erreur lors de ${isEditMode.value ? 'la modification' : 'la création'}`, { position: 'top-right' })
  } finally {
    isSubmitting.value = false
  }
}
</script>
